<template>
  <div class="min-h-screen bg-gradient-to-br from-orange-50 to-white flex items-center justify-center p-4 overflow-hidden relative">
    <!-- Floating animated shapes in background -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
          v-for="(shape, index) in floatingShapes"
          :key="index"
          :class="[
          'absolute rounded-full opacity-20',
          shape.color,
          shape.animation
        ]"
          :style="{
          width: shape.size,
          height: shape.size,
          left: shape.left,
          top: shape.top,
          animationDelay: shape.delay
        }"
      />
    </div>

    <!-- Main content -->
    <div class="relative z-10 text-center max-w-2xl mx-auto">
      <!-- Bouncing icon -->
      <div class="mb-8 animate-bounce-slow">
        <svg
            class="w-24 h-24 mx-auto text-orange-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <!-- 404 Text with scale animation -->
      <h1
          class="text-7xl md:text-9xl font-bold text-orange-600 mb-4 animate-scale-in"
          style="color: #ea580c"
      >
        404
      </h1>

      <!-- Page Not Found text -->
      <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-4 animate-fade-in">
        Page Not Found
      </h2>

      <!-- Subtext -->
      <p class="text-gray-500 text-lg mb-8 animate-fade-in-delay">
        Oops! The page you're looking for doesn't exist.
      </p>

      <!-- Go Home Button -->
      <button
          @click="goHome"
          class="group relative px-8 py-4 bg-orange-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-out animate-fade-in-delay-2"
          style="background-color: #ea580c"
      >
        <span class="relative z-10">Go Home</span>
        <div class="absolute inset-0 bg-orange-700 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300 ease-out" />
      </button>

      <!-- Decorative elements -->
      <div class="mt-12 flex justify-center gap-4">
        <div
            v-for="dot in 3"
            :key="dot"
            class="w-3 h-3 bg-orange-600 rounded-full animate-pulse"
            :style="{ animationDelay: `${dot * 200}ms`, backgroundColor: '#ea580c' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const floatingShapes = ref([
  { size: '120px', color: 'bg-orange-300', left: '10%', top: '20%', animation: 'animate-float-slow', delay: '0s' },
  { size: '80px', color: 'bg-orange-400', left: '80%', top: '30%', animation: 'animate-float-medium', delay: '1s' },
  { size: '100px', color: 'bg-orange-200', left: '15%', top: '70%', animation: 'animate-float-fast', delay: '0.5s' },
  { size: '60px', color: 'bg-orange-500', left: '85%', top: '75%', animation: 'animate-float-slow', delay: '1.5s' },
  { size: '90px', color: 'bg-orange-300', left: '50%', top: '10%', animation: 'animate-float-medium', delay: '0.8s' },
  { size: '70px', color: 'bg-orange-400', left: '25%', top: '50%', animation: 'animate-float-fast', delay: '1.2s' },
])

const goHome = () => {
  window.location.href = '/'
}
</script>

<style scoped>
@keyframes float-slow {
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-20px) translateX(10px);
  }
  50% {
    transform: translateY(-40px) translateX(-10px);
  }
  75% {
    transform: translateY(-20px) translateX(10px);
  }
}

@keyframes float-medium {
  0%, 100% {
    transform: translateY(0) translateX(0) rotate(0deg);
  }
  33% {
    transform: translateY(-30px) translateX(15px) rotate(120deg);
  }
  66% {
    transform: translateY(-15px) translateX(-15px) rotate(240deg);
  }
}

@keyframes float-fast {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-50px) scale(1.1);
  }
}

@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes scale-in {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-float-slow {
  animation: float-slow 6s ease-in-out infinite;
}

.animate-float-medium {
  animation: float-medium 5s ease-in-out infinite;
}

.animate-float-fast {
  animation: float-fast 4s ease-in-out infinite;
}

.animate-bounce-slow {
  animation: bounce-slow 2s ease-in-out infinite;
}

.animate-scale-in {
  animation: scale-in 0.6s ease-out;
}

.animate-fade-in {
  animation: fade-in 0.8s ease-out 0.3s both;
}

.animate-fade-in-delay {
  animation: fade-in 0.8s ease-out 0.5s both;
}

.animate-fade-in-delay-2 {
  animation: fade-in 0.8s ease-out 0.7s both;
}
</style>